/** @jsxImportSource @emotion/react */
// import { css } from "@emotion/react";
import React, { useState, useEffect, useRef } from "react";
import { gql, useMutation } from '@apollo/client';
import { useSelector } from "react-redux";
import LayoutWrapper from "./LayoutWrapper";
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { MULTI_FIELD_UPDATE_MUTATION } from "../mutations/mutations";
import { MULTI_FIELD_READ_MUTATION } from "./adapters/utils/constants";
import { Button } from 'primereact/button';
import { marketplaceDataLoadStyle } from "./common/marketplace/styles";
import { Toast } from 'primereact/toast';

const BANNER_MUTATION = gql`
    mutation bannerMutation($domainId: String, $slotName: String, $tagId: String) {
        bannerMutation(input: {domainId: $domainId, slotName: $slotName, tagId: $tagId}) {
                response
        }
    }
`;

const VIDEO_MUTATION = gql`
    mutation videoMutation($domainId: String, $slotName: String, $tagId: String) {
        videoMutation(input: {domainId: $domainId, slotName: $slotName, tagId: $tagId}) {
                response
        }
    }
`;

export default function AdapterTagId() {
    const [multiFieldUpdateMutation] = useMutation(MULTI_FIELD_UPDATE_MUTATION);
    const [multiFieldRetrieveMutation] = useMutation(MULTI_FIELD_READ_MUTATION);

    const { currentDomain, currentCustomer } = useSelector(state => state.userInformation);
    const [activeIndex, setActiveIndex] = useState(0);
    const [bannerTagIds, setBannerTagIds] = useState([]);
    const [videoTagIds, setVideoTagIds] = useState([]);
    const [isRetrievingDatas, setIsRetrievingDatas] = useState(false);
    const [bannerMutation] = useMutation(BANNER_MUTATION);
    const [videoMutation] = useMutation(VIDEO_MUTATION);
    const toast = useRef(null);

    const addRow = (type) => {
        if (type === 'banner') {
            setBannerTagIds([...bannerTagIds, { placement: "", tagid: "" }]);
        } else {
            setVideoTagIds([...videoTagIds, { placement: "", tagid: "" }]);
        }
    };

    const removeRow = (type, index) => {
        if (type === 'banner') {
            setBannerTagIds(bannerTagIds.filter((_, i) => i !== index));
        } else {
            setVideoTagIds(videoTagIds.filter((_, i) => i !== index));
        }
    };

    const showMessages = (messageType, messageBody, summary) => {
        toast.current.show({ severity: messageType, summary: summary, detail: messageBody });
    };

    useEffect(() => {
        if (currentDomain && currentCustomer) {
            setIsRetrievingDatas(true);
            multiFieldRetrieveMutation({
                variables: {
                    fieldDefinitions: JSON.stringify([
                        { field_name: 'serverside_tagid_configuration', post_id: currentDomain, parse: true },
                        { field_name: 'serverside_video_tagid_configuration', post_id: currentDomain, parse: true }
                    ])
                }
            }).then(response => {
                let data = JSON.parse(response.data.multiFieldRetrieveMutation.response);
                data = Object.values(data).filter(item => item !== null);

                if (data.length > 0) {
                    const bannerData = data.find(item => item.mediatype === 'banner');
                    const videoData = data.find(item => item.mediatype === 'video');
                    if (bannerData) {
                        setBannerTagIds(bannerData.tagids);
                    }
                    if (videoData) {
                        setVideoTagIds(videoData.tagids);
                    }
                }

                setIsRetrievingDatas(false);
            }).catch(err => {
                console.log({ err });
                setIsRetrievingDatas(false);
            });
        }
    }, [currentDomain, currentCustomer, multiFieldRetrieveMutation]);

    async function saveConfiguration() {
        const mediatype = activeIndex === 0 ? 'banner' : 'video';
        const tagids = mediatype === 'banner' ? bannerTagIds : videoTagIds;

        const updatedData = {
            mediatype,
            tagids: tagids.map(item => ({
                placement: item.placement,
                tagid: item.tagid
            }))
        };

        console.log({ updatedData });

        const emptyPlacementIndexes = updatedData.tagids
            .map((item, index) => ({ placement: item.placement, index }))
            .filter(({ placement }) => !placement)
            .map(({ index }) => index);

        const emptyTagIdIndexes = updatedData.tagids
            .map((item, index) => ({ tagid: item.tagid, index }))
            .filter(({ tagid }) => !tagid)
            .map(({ index }) => index);

        if (emptyPlacementIndexes.length > 0) {
            showMessages('error', 'Placement cannot be empty', 'Error');
            return;
        }

        if (emptyTagIdIndexes.length > 0) {
            showMessages('error', 'Tag ID cannot be empty', 'Error');
            return;
        }

        showMessages('info', 'Saving Data....', 'Success');

        const fieldName = mediatype === 'banner' ? 'serverside_tagid_configuration' : 'serverside_video_tagid_configuration';

        try {
            await multiFieldUpdateMutation({
                variables: {
                    rules: JSON.stringify([
                        {
                            field_name: fieldName,
                            post_id: currentDomain,
                            value: JSON.stringify(updatedData)
                        }
                    ])
                }
            });

            if (mediatype === 'banner') {
                await bannerMutation({ variables: { domainId: String(currentDomain), slotName: 'bannerSlot', tagId: JSON.stringify(updatedData.tagids) } });
            } else if (mediatype === 'video') {
                await videoMutation({ variables: { domainId: String(currentDomain), slotName: 'videoSlot', tagId: JSON.stringify(updatedData.tagids) } });
            }
        } catch (error) {
            showMessages('error', 'Failed to save data', 'Error');
        }
    }

    return (
        <LayoutWrapper
            model={"adapter"}
            renderOpsBar={true}
            saveCallback={saveConfiguration}
        >
            <h2>Serverside tagId mapping</h2>

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel header="Banner">
                    <div className="card">
                        <style>
                            {`
                                .input-container {
                                    display: flex;
                                    align-items: center;
                                    gap: 10px;
                                    flex-wrap: wrap;
                                }

                                .tag-id {
                                    flex: 1;
                                    min-width: 200px;
                                }

                                .placement-input {
                                    flex: 1;
                                    min-width: 200px;
                                    margin: 5px;
                                }

                                .remove-button {
                                    background: red;
                                    color: white;
                                    border: none;
                                    padding: 5px 10px;
                                    cursor: pointer;
                                    border-radius: 4px;
                                }
                            `}
                        </style>

                        {bannerTagIds.map((item, index) => (
                            <div key={index} className="input-container">
                                <InputText
                                    className="placement-input"
                                    value={item.placement}
                                    onChange={(e) => {
                                        const updatedTagids = [...bannerTagIds];
                                        updatedTagids[index].placement = e.target.value;
                                        setBannerTagIds(updatedTagids);
                                    }}
                                    placeholder="Enter Placement"
                                />
                                <InputText
                                    className="tag-id"
                                    value={item.tagid}
                                    onChange={(e) => {
                                        const updatedTagids = [...bannerTagIds];
                                        updatedTagids[index].tagid = e.target.value;
                                        setBannerTagIds(updatedTagids);
                                    }}
                                    placeholder="Enter Tag ID"
                                />
                                <Button
                                    onClick={() => removeRow('banner', index)}
                                    className="remove-button"
                                    icon="pi pi-times" rounded outlined severity="danger" aria-label="Cancel"
                                />
                            </div>
                        ))}

                        <div className="card flex justify-content-center">
                            <Button onClick={() => addRow('banner')} label="Add Row" />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel header="Video">
                    <div className="card">
                        <style>
                            {`
                                .input-container {
                                    display: flex;
                                    align-items: center;
                                    gap: 10px;
                                    flex-wrap: wrap;
                                }

                                .tag-id {
                                    flex: 2;
                                    min-width: 200px;
                                }

                                .placement-input {
                                    flex: 1;
                                    min-width: 200px;
                                    margin: 5px;
                                }

                                .remove-button {
                                    background: red;
                                    color: white;
                                    border: none;
                                    padding: 5px 10px;
                                    cursor: pointer;
                                    border-radius: 4px;
                                }
                            `}
                        </style>

                        {videoTagIds.map((item, index) => (
                            <div key={index} className="input-container">
                                <InputText
                                    className="placement-input"
                                    value={item.placement}
                                    onChange={(e) => {
                                        const updatedTagids = [...videoTagIds];
                                        updatedTagids[index].placement = e.target.value;
                                        setVideoTagIds(updatedTagids);
                                    }}
                                    placeholder="Enter Placement"
                                />
                                <InputText
                                    className="tag-id"
                                    value={item.tagid}
                                    onChange={(e) => {
                                        const updatedTagids = [...videoTagIds];
                                        updatedTagids[index].tagid = e.target.value;
                                        setVideoTagIds(updatedTagids);
                                    }}
                                    placeholder="Enter Tag ID"
                                />
                                <Button
                                    onClick={() => removeRow('video', index)}
                                    className="remove-button"
                                    icon="pi pi-times" rounded outlined severity="danger" aria-label="Cancel"
                                />
                            </div>
                        ))}
                        <div className="card flex justify-content-center">
                            <Button onClick={() => addRow('video')} label="Add Row" />
                        </div>
                    </div>
                </TabPanel>
            </TabView>
            <div css={marketplaceDataLoadStyle} className={isRetrievingDatas ? 'flex' : 'd-none'}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "3em", color: "#fff" }}></i>
                <p className="message">Retrieving TagIDs mapping data</p>
            </div>

            <div className="card flex justify-content-center">
                <Toast ref={toast} position="bottom-right" />
            </div>
        </LayoutWrapper>
    );
}
